import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useJobMatches } from '../hooks/useJobMatches'
import type { JobMatchFilters } from '../hooks/useJobMatches'
import type { JobMatch } from '../types/api'
import styles from './JobMatchesPage.module.css'

const TIER_FILTERS: Array<{ value: number | null; label: string }> = [
  { value: null, label: '전체' },
  { value: 1, label: 'Tier 1 도전' },
  { value: 2, label: 'Tier 2' },
  { value: 3, label: 'Tier 3' },
]

const MIN_MATCH_OPTIONS = [
  { value: null, label: '전체 매칭률' },
  { value: 70, label: '70% 이상' },
  { value: 50, label: '50% 이상' },
  { value: 30, label: '30% 이상' },
]

const SOURCE_META: Record<string, { label: string; cls: string }> = {
  wanted: { label: 'Wanted', cls: styles.sourceWanted },
  linkedin: { label: 'LinkedIn', cls: styles.sourceLinkedin },
  jobkorea: { label: 'JobKorea', cls: styles.sourceJobkorea },
}

function SourceBadge({ source }: { source: string }) {
  const meta = SOURCE_META[source] ?? { label: source, cls: styles.sourceEtc }
  return <span className={`${styles.sourceBadge} ${meta.cls}`}>{meta.label}</span>
}

function TierBadge({ tier }: { tier: number }) {
  const cls =
    tier === 1 ? styles.tier1 : tier === 2 ? styles.tier2 : styles.tier3
  const label = tier === 1 ? 'Tier 1 도전' : `Tier ${tier}`
  return <span className={`${styles.tierBadge} ${cls}`}>{label}</span>
}

function MatchBar({ pct }: { pct: number }) {
  const color =
    pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)'
  return (
    <div className={styles.matchBar}>
      <div className={styles.matchTrack}>
        <div
          className={styles.matchFill}
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className={styles.matchValueWrap}>
        <span className={styles.matchValue} style={{ color }}>
          {pct}%
        </span>
        <span className={styles.estimate}>추정치</span>
      </div>
    </div>
  )
}

function SkillChips({
  skills,
}: {
  skills: { met: string[]; partial: string[]; missing: string[] } | null
}) {
  if (!skills) return null
  const hasAny =
    skills.met.length + skills.partial.length + skills.missing.length > 0
  if (!hasAny) return null
  return (
    <div className={styles.skillWrap}>
      {skills.met.length > 0 && (
        <div className={styles.skillRow}>
          <span className={`${styles.skillLabel} ${styles.skillMet}`}>
            충족
          </span>
          {skills.met.map((s, i) => (
            <span key={i} className={`${styles.skillChip} ${styles.chipMet}`}>
              {s}
            </span>
          ))}
        </div>
      )}
      {skills.partial.length > 0 && (
        <div className={styles.skillRow}>
          <span className={`${styles.skillLabel} ${styles.skillPartial}`}>
            부분
          </span>
          {skills.partial.map((s, i) => (
            <span key={i} className={`${styles.skillChip} ${styles.chipPartial}`}>
              {s}
            </span>
          ))}
        </div>
      )}
      {skills.missing.length > 0 && (
        <div className={styles.skillRow}>
          <span className={`${styles.skillLabel} ${styles.skillMissing}`}>
            미충족
          </span>
          {skills.missing.map((s, i) => (
            <span key={i} className={`${styles.skillChip} ${styles.chipMissing}`}>
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function JobCard({
  job,
  expanded,
  onToggle,
}: {
  job: JobMatch
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={`${styles.card} ${expanded ? styles.cardExpanded : ''}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
    >
      <div className={styles.cardTop}>
        <div className={styles.cardCompany}>{job.company}</div>
        <SourceBadge source={job.source} />
      </div>
      <div className={styles.cardPosition}>{job.position}</div>

      <div className={styles.cardMeta}>
        <TierBadge tier={job.tier} />
        <span
          className={
            job.jdVerified ? styles.verifiedYes : styles.verifiedNo
          }
        >
          {job.jdVerified ? '✓ JD 검증' : '미검증'}
        </span>
      </div>

      <MatchBar pct={job.matchPct} />

      {expanded && (
        <div className={styles.detailPanel}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>매칭률</span>
            <span className={styles.detailValue}>
              {job.matchPct}% <span className={styles.detailHint}>(추정치)</span>
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>티어</span>
            <span className={styles.detailValue}>
              {job.tier === 1 ? 'Tier 1 · 도전' : `Tier ${job.tier}`}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>JD 검증</span>
            <span className={styles.detailValue}>
              {job.jdVerified ? '검증됨' : '미검증'}
            </span>
          </div>
          {job.sourceUrl && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>공고 URL</span>
              <a
                className={styles.detailLink}
                href={job.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {job.sourceUrl} ↗
              </a>
            </div>
          )}
          {job.jdUrl && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>JD URL</span>
              <a
                className={styles.detailLink}
                href={job.jdUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {job.jdUrl} ↗
              </a>
            </div>
          )}
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>수집일</span>
            <span className={styles.detailValueMono}>
              {new Date(job.fetchedAt).toLocaleString('ko-KR')}
            </span>
          </div>
        </div>
      )}

      <SkillChips skills={job.skills} />

      <div className={styles.cardFooter}>
        <span className={styles.fetchedAt}>
          {new Date(job.fetchedAt).toLocaleDateString('ko-KR')}
        </span>
        {job.sourceUrl && (
          <a
            className={styles.sourceLink}
            href={job.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            공고 보기 ↗
          </a>
        )}
      </div>
    </div>
  )
}

export default function JobMatchesPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const [tierFilter, setTierFilter] = useState<number | null>(null)
  const [minMatch, setMinMatch] = useState<number | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const filters: JobMatchFilters = { tier: tierFilter, minMatch }
  const { jobMatches, loading, error, reload, refresh, refreshing } =
    useJobMatches(talentId, filters)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }, [])

  const handleRefresh = async () => {
    if (refreshing) return
    try {
      const result = await refresh()
      showToast(`✦ ${result.created}개 공고 크롤링 완료`)
    } catch (e) {
      showToast(`⚠ ${e instanceof Error ? e.message : '크롤링 실패'}`)
    }
  }

  if (!talentId) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>채용 매칭</h1>
        </div>
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>🔍</div>
          <div>인재를 먼저 선택하세요</div>
          <div className={styles.emptyHint}>
            대시보드에서 인재를 선택해 주세요
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>채용 매칭</h1>
        <button
          className={styles.crawlBtn}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <span className={styles.spinner} />
          ) : (
            '✦'
          )}{' '}
          {refreshing ? '크롤링 중…' : '새 공고 크롤링'}
        </button>
      </div>

      <div className={styles.filters}>
        <div className={styles.chipGroup}>
          {TIER_FILTERS.map((t) => (
            <button
              key={String(t.value)}
              className={`${styles.chip} ${
                tierFilter === t.value ? styles.chipActive : ''
              }`}
              onClick={() => setTierFilter(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          className={styles.minMatchSelect}
          value={String(minMatch)}
          onChange={(e) => {
            const v = e.target.value
            setMinMatch(v === 'null' ? null : Number(v))
          }}
        >
          {MIN_MATCH_OPTIONS.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {toast && (
        <div className={styles.toast}>{toast}</div>
      )}

      {loading ? (
        <div className={styles.grid}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      ) : error ? (
        <div className={styles.errorWrap}>
          <span className={styles.errorMsg}>{error}</span>
          <button className={styles.retryBtn} onClick={reload}>
            다시 시도
          </button>
        </div>
      ) : jobMatches.length === 0 ? (
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>📡</div>
          <div>아직 매칭된 공고가 없습니다</div>
          <div className={styles.emptyHint}>
            위쪽 '새 공고 크롤링' 버튼을 눌러 실행하세요
          </div>
        </div>
      ) : (
        <div className={styles.grid}>
          {jobMatches.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              expanded={selectedId === job.id}
              onToggle={() =>
                setSelectedId(selectedId === job.id ? null : job.id)
              }
            />
          ))}
        </div>
      )}
    </>
  )
}
